import React from "react";
import { Pipette } from "lucide-react";
import HexColorPopover from "./HexColorPopover";
import { cn } from "../../utils/classMerger";

interface ColorFieldProps {
  label: string;
  color: string;
  disabled?: boolean;
  onChange: (color: string) => void;
}

// Shared brand-colour card: swatch + label + hex, click to edit via
// HexColorPopover. Used by the captioned-video flow, the brand colour selector
// and the admin brokerage (Masters) editor so they share the exact same UI.
const ColorField: React.FC<ColorFieldProps> = ({
  label,
  color,
  disabled = false,
  onChange,
}) => {
  const trigger = (
    <button
      type="button"
      disabled={disabled}
      aria-label={disabled ? `${label} preview` : `${label} picker`}
      className={cn(
        "flex items-center gap-2 p-2 rounded-md w-full text-left min-w-0",
        disabled ? "cursor-default" : "cursor-pointer hover:bg-neutral-50",
      )}
    >
      <span
        className="relative inline-block w-9 h-9 rounded-md border border-neutral-200 flex-shrink-0"
        style={{ backgroundColor: color }}
      >
        {!disabled && (
          <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white border border-neutral-300 shadow-sm">
            <Pipette className="w-2.5 h-2.5 text-primary" />
          </span>
        )}
      </span>
      <span className="flex flex-col leading-tight min-w-0">
        <span className="text-primary font-semibold text-sm truncate">
          {label}
        </span>
        <span className="text-xs text-neutral-500 font-mono">
          {color.toUpperCase()}
        </span>
      </span>
    </button>
  );

  if (disabled) return trigger;

  return (
    <HexColorPopover color={color} onChange={onChange}>
      {trigger}
    </HexColorPopover>
  );
};

export default ColorField;
